import { useState } from 'react'
import { toast } from '@/hooks/use-toast'

const footerSections = [
  {
    title: "Contact",
    links: [
      { name: "contact@wememberz.com", href: "mailto:contact@wememberz.com" },
      { name: "FAQ", href: "#faq" },
      { name: "Comment ça marche", href: "#concept" }
    ]
  }
]

export default function Footer() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !email.includes('@')) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer une adresse email valide.",
        variant: "destructive"
      })
      return
    }

    setIsSubmitting(true)
    
    try {
      const response = await fetch('/save_email_newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      })

      if (response.ok) {
        toast({
          title: "Merci pour votre inscription !",
          description: "Vous recevrez bientôt nos meilleures offres et conseils.",
        })
        setEmail('')
      } else {
        throw new Error('Erreur lors de l\'inscription')
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'inscription. Veuillez réessayer.",
        variant: "destructive"
      })
    } finally {
      setIsSubmitting(false)
    }
  }
  return (
    <footer className="bg-earth-brown/5 border-t border-border/50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Main Footer */}
        <div className="py-8 lg:py-12">
          <div className="grid lg:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {/* Brand */}
            <div className="space-y-6">
              <div className="flex items-center space-x-2 justify-center">
                <div className="w-10 h-10 rounded-xl overflow-hidden">
                  <img src="/images/logo_square.png" alt="Wememberz" className="w-full h-full object-cover" />
                </div>
                <span className="font-display font-semibold text-2xl text-foreground">
                  Wememberz
                </span>
              </div>
              
              {/* Social */}
              <div className="flex justify-center space-x-3">
                <a href="https://www.instagram.com/we.memberz/?hl=fr" target="blank" aria-label="Wememberz sur Instagram" className="w-10 h-10 bg-soft-cream hover:bg-[#f9bb72] rounded-lg flex items-center justify-center group transition-smooth">
                  <svg className="w-5 h-5 text-muted-foreground group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://www.facebook.com/p/Wememberz-100080230132465" target="blank" aria-label="Wememberz sur Facebook" className="w-10 h-10 bg-soft-cream hover:bg-[#f9bb72] rounded-lg flex items-center justify-center group transition-smooth">
                  <svg className="w-5 h-5 text-muted-foreground group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
                  </svg>
                </a>
                <a href="https://www.tiktok.com/@wememberz" target="blank" aria-label="Wememberz sur TikTok" className="w-10 h-10 bg-soft-cream hover:bg-[#f9bb72] rounded-lg flex items-center justify-center group transition-smooth">
                  <svg className="w-5 h-5 text-muted-foreground group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                </a>
                <a href="https://fr.linkedin.com/company/wememberz" target="blank" aria-label="Wememberz sur LinkedIn" className="w-10 h-10 bg-soft-cream hover:bg-[#f9bb72] rounded-lg flex items-center justify-center group transition-smooth">
                  <svg className="w-5 h-5 text-muted-foreground group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>

              {/* Contact Email */}
              <div className="pt-2 text-center">
                <a 
                  href="mailto:contact@wememberz.com"
                  className="text-muted-foreground hover:text-primary transition-smooth"
                >
                  contact@wememberz.com
                </a>
              </div>
            </div>

            {/* Newsletter Section */}
            <div className="space-y-6 text-center">
              <h3 className="text-xl font-display font-semibold text-foreground">
                Ne ratez aucune promo ni offre flash !
              </h3>
              <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto space-y-3">
                <input 
                  type="email"
                  placeholder="Votre adresse email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-[#f9bb72]/50 focus:border-[#f9bb72] disabled:opacity-50"
                />
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-hero-gradient hover:opacity-90 text-white px-6 py-3 rounded-lg font-semibold transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Inscription...' : "S'inscrire"}
                </button>
              </form>
              <p className="text-xs text-muted-foreground">
                Pas de spam, juste nos meilleures offres et conseils.
              </p>
            </div>
          </div>
        </div>


        {/* Bottom Footer */}
        <div className="py-6 border-t border-border/50">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Wememberz. Tous droits réservés.
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <a href="/mentions_legales" className="text-muted-foreground hover:text-primary transition-smooth">
                Mentions Légales & CGU
              </a>
              <a href="/politique_confidentialite" className="text-muted-foreground hover:text-primary transition-smooth">
                Politique de confidentialité
              </a>
              <a href="/cgv" className="text-muted-foreground hover:text-primary transition-smooth">
                CGV
              </a>
              <a href="/faq" className="text-muted-foreground hover:text-primary transition-smooth">
                FAQ
              </a>
              <a href="/qui_sommes_nous" className="text-muted-foreground hover:text-primary transition-smooth">
                Qui sommes-nous ?
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}